import cx from "classnames";
import { useMemo } from "react";

import { useFormContext } from "../../hooks/useFormContext";
import { useFormFieldVisibility } from "../../hooks/useFormFieldVisibility";
import { $t } from "../../utils/intl";
import { FormConfig, FormValues, FormPage } from "../../types/form";
import { SmartDocRenderer } from "../common/SmartDocRenderer/SmartDocRenderer";
import { FormPageNavigation } from "../FormPageNavigation/FormPageNavigation";
import {
  formatReviewFieldValue,
  getReviewFieldControl,
  getReviewFieldLabel,
  getReviewPageFieldItems,
  getReviewFieldSlugs,
} from "./reviewPage.helpers";
import { reviewPageText } from "./ReviewPage.text";

import "./ReviewPage.sass";

interface ReviewPageProps {
  config: FormConfig;
  formPages: FormPage[];
  isSubmitting: boolean;
  onEditPage: (index: number) => void;
  onPrevious: () => void;
  onSubmit: () => void;
  reviewPage: FormPage;
  submitLabel: string;
  values: FormValues;
}

export const ReviewPage: React.FC<ReviewPageProps> = ({
  config,
  formPages,
  isSubmitting,
  onEditPage,
  onPrevious,
  onSubmit,
  reviewPage,
  submitLabel,
  values,
}) => {
  const { preferences } = useFormContext();
  const appFieldMap = useMemo(
    () => new Map(config.application.structure.map((field) => [field.slug, field])),
    [config.application.structure]
  );
  const allItems = useMemo(() => formPages.flatMap((page) => page.items), [formPages]);
  const { checkFieldVisibility } = useFormFieldVisibility(allItems, values, appFieldMap);

  const unansweredLabel = $t(reviewPageText.unanswered);
  const selectedFieldSlugs = useMemo(() => new Set(getReviewFieldSlugs(reviewPage)), [reviewPage]);
  const reviewTitle = reviewPage.review?.title;
  const reviewDescription = reviewPage.review?.description;

  const groups = useMemo(
    () =>
      formPages
        .map((page, pageIndex) => {
          const fields = getReviewPageFieldItems(page)
            .filter(({ item, parentSectionSlug }) => {
              if (!selectedFieldSlugs.has(item.slug)) return false;
              if (parentSectionSlug && !checkFieldVisibility(parentSectionSlug)) return false;
              return checkFieldVisibility(item.slug);
            })
            .map(({ item }) => {
              const appField = appFieldMap.get(item.slug);
              return {
                appField,
                formItem: item,
                label: getReviewFieldLabel(item, appField),
                rawValue: values[item.slug],
                reviewControl: getReviewFieldControl(appField),
                slug: item.slug,
                value: formatReviewFieldValue({
                  appField,
                  preferences,
                  unansweredLabel,
                  value: values[item.slug],
                }),
              };
            });

          return fields.length > 0
            ? {
                fields,
                index: pageIndex,
                name: page.name,
              }
            : null;
        })
        .filter((group): group is NonNullable<typeof group> => !!group),
    [
      appFieldMap,
      checkFieldVisibility,
      formPages,
      preferences,
      selectedFieldSlugs,
      unansweredLabel,
      values,
    ]
  );

  const shouldShowTitle = reviewTitle !== "" && reviewTitle !== null;
  const shouldShowDescription = reviewDescription !== "" && reviewDescription !== null;

  return (
    <section className="review-page" data-testid="review-page">
      {(shouldShowTitle || shouldShowDescription) && (
        <div className="review-page__header">
          {shouldShowTitle && (
            <h2 className="review-page__title">{reviewTitle ?? $t(reviewPageText.defaultTitle)}</h2>
          )}

          {shouldShowDescription &&
            (typeof reviewDescription === "string" || reviewDescription === undefined ? (
              <p className="review-page__description">
                {reviewDescription ?? $t(reviewPageText.defaultDescription)}
              </p>
            ) : (
              <SmartDocRenderer
                className="review-page__description"
                editorData={reviewDescription}
              />
            ))}
        </div>
      )}

      <div className="review-page__groups">
        {groups.map((group) => (
          <section className="review-page__group" key={group.name}>
            <header className="review-page__group-header">
              <h3 className="review-page__group-title">{group.name}</h3>
              <button
                className="review-page__edit-button"
                onClick={() => onEditPage(group.index)}
                type="button"
              >
                {$t(reviewPageText.edit)}
              </button>
            </header>

            <dl className="review-page__fields">
              {group.fields.map((field) => {
                const isUnanswered = field.value === unansweredLabel;
                // Rendered only for an answered field: an empty value has no rows to show and
                // must fall back to the "Unanswered" copy like every other field.
                const ReviewControl = isUnanswered ? undefined : field.reviewControl;

                return (
                  <div className="review-page__field" key={field.slug}>
                    <dt className="review-page__field-label">{field.label}</dt>
                    <dd
                      className={cx("review-page__field-value", {
                        "review-page__field-value--unanswered": isUnanswered,
                      })}
                    >
                      {ReviewControl && field.appField ? (
                        <ReviewControl
                          appField={field.appField}
                          formItem={field.formItem}
                          preferences={preferences}
                          value={field.rawValue}
                        />
                      ) : (
                        field.value
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>
        ))}
      </div>

      <FormPageNavigation
        backLabel={$t(reviewPageText.back)}
        forcePrimarySubmit={true}
        isFirstPage={false}
        isLastPage={false}
        isSinglePage={false}
        isSubmitting={isSubmitting}
        onNext={onSubmit}
        onPrevious={onPrevious}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
      />
    </section>
  );
};
